import {
  ChartLineIcon,
  LinkIcon,
  MessageSquarePlusIcon,
  MousePointerClickIcon,
  SearchIcon,
  SparklesIcon,
} from '@air/design-icons';

import { type WorkspaceAgentIconComponent } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/types';

/** The role question the intro agent asks before it hands off to the workspace. */
export const WORKSPACE_AGENT_ONBOARDING_ROLE_OPTIONS = [
  'Creative',
  'Marketing',
  'Product Design',
  'Founder / Executive',
  'Other',
] as const;

export type WorkspaceAgentOnboardingRole = (typeof WORKSPACE_AGENT_ONBOARDING_ROLE_OPTIONS)[number];

export type WorkspaceAgentOnboardingTimeSink = {
  Icon: WorkspaceAgentIconComponent;
  id: string;
  label: string;
  /** The workspace-agent header once Next lands on this run. */
  panelTitle: string;
  /**
   * Lower numbers run first. `0` is last: Analyzing stays behind every other selected pill.
   */
  priority: number;
};

/**
 * The second question in the intro, revealed once a role is picked. Order matches the prototype.
 * Next stays quiet until at least one of these is picked.
 */
export const WORKSPACE_AGENT_ONBOARDING_TIME_SINKS = [
  {
    id: 'collaboration',
    label: 'Feedback + approvals',
    panelTitle: 'Get status',
    Icon: MessageSquarePlusIcon,
    priority: 1,
  },
  {
    id: 'multiSelect',
    label: 'Organizing my library',
    panelTitle: 'Bulk tag & metadata',
    Icon: MousePointerClickIcon,
    priority: 2,
  },
  {
    id: 'generation',
    label: 'Making variants of assets',
    panelTitle: 'Generate variants',
    Icon: SparklesIcon,
    priority: 3,
  },
  { id: 'distribution', label: 'Sharing out work', panelTitle: 'Rename files in bulk', Icon: LinkIcon, priority: 5 },
  { id: 'find', label: 'Finding things I need', panelTitle: 'Prep for Annie Quinn', Icon: SearchIcon, priority: 6 },
  {
    id: 'analytics',
    label: 'Analyzing performance',
    panelTitle: 'Resize your top ad',
    Icon: ChartLineIcon,
    priority: 0,
  },
] as const satisfies readonly WorkspaceAgentOnboardingTimeSink[];

export type WorkspaceAgentOnboardingTimeSinkId = (typeof WORKSPACE_AGENT_ONBOARDING_TIME_SINKS)[number]['id'];

/**
 * Jobs the live agent can already run after the scripted example. Sharing and performance stay
 * on that example: those answers are not a real send.
 */
export const WORKSPACE_AGENT_ONBOARDING_LIVE_AGENT_PROMPT_BY_TIME_SINK_ID: Partial<
  Record<WorkspaceAgentOnboardingTimeSinkId, string>
> = {
  collaboration: 'What assets have open comments that need attention?',
  find: 'What can you tell me about the most recent few assets uploaded to my workspace?',
  generation: 'Can you make a 4:3 and 9:16 variant of the most recent image uploaded to my workspace?',
  multiSelect:
    'Find a few assets that have versions and comments in my workspace that are untagged and suggest some tags to help organize them.',
};

export type WorkspaceAgentOnboardingAnswers = {
  role: WorkspaceAgentOnboardingRole;
  timeSinkIds: readonly WorkspaceAgentOnboardingTimeSinkId[];
};

export const isWorkspaceAgentOnboardingRole = (value: string): value is WorkspaceAgentOnboardingRole =>
  WORKSPACE_AGENT_ONBOARDING_ROLE_OPTIONS.some((role) => role === value);

export const isWorkspaceAgentOnboardingTimeSinkId = (value: string): value is WorkspaceAgentOnboardingTimeSinkId =>
  WORKSPACE_AGENT_ONBOARDING_TIME_SINKS.some((timeSink) => timeSink.id === value);
